import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  WEB_MAX_CONTENT_WIDTH,
  WEB_MODAL_MAX_WIDTH,
} from '../../theme/webLayoutWidths';

import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import { useTheme, GTFonts } from '../../theme';
import {
  fs,
  s,
  v,
  formatWithCommas,
  formatNumberDecimal,
  getCurrencyDisplayFormat,
  getNumericDisplayDecimals,
  iconSize,
} from '../../utils/libs';
import AppDataTable, {
  type AppDataTableColumn,
} from '../../components/table/AppDataTable';
import {
  getDashboardTotals,
  getCurrentAndLastMonthSales,
  getWeeklyMostSelling as apiGetWeeklyMostSelling,
  getCategorySalesReport,
  getLastFourWeeksSales,
  getPaymentMethodBreakdown,
} from '../../api/backoffice';
import WeeklyComparisonChart from '../../components/charts/WeeklyComparisonChart';
import CategoryDonutChart from '../../components/charts/CategoryDonutChart';
import MoneyCollectedCard from '../../components/backoffice/MoneyCollectedCard';
import StillOwedCard from '../../components/backoffice/StillOwedCard';
// import PaymentMethodStackedChart from '../../components/charts/PaymentMethodStackedChart';
import {
  buildPaymentMethodColorMap,
  // getSeriesChartTitle,
  sortMethodsByNetDesc,
} from '../../utils/paymentBreakdown';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  setDashboardData,
  setDashboardError,
  setDashboardLoading,
  setPaymentBreakdown,
  setPaymentBreakdownError,
  setPaymentBreakdownLoading,
  type DashboardComparisonMetric,
} from '../../redux/backoffice/dashboardSlice';
import {
  setCurrentAndLastMonth,
  setWeeklySalesData,
} from '../../redux/backoffice/reportsSlice';
import {
  setCategorySalesFromApi,
  setLoadingCategory,
  setErrorCategory,
} from '../../redux/backoffice/salesSlice';
import { setWeeklyMostSelling } from '../../redux/backoffice/productsSlice';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import BackOfficeFallback from '../../components/Reusables/BackOfficeFallback';
import {
  getMonthlySalesTarget,
  addMonthlySalesTarget,
} from '../../api/backofficeReports';
import {
  Button,
  Dialog,
  Portal,
  TextInput,
  useTheme as usePaperTheme,
} from 'react-native-paper';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import {
  setTargetAmount,
  setTargetContext,
  setTargetLoading,
  setTargetError,
} from '../../redux/backoffice/targetsSlice';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';
import GreenActionButton from '../../components/Reusables/GreenActionButton';
import {
  getDashboardComparisonPeriodLabel,
  getDashboardDateRange,
  getDashboardRangeDisplayLabel,
  type DashboardPeriodKey,
} from '../../utils/dashboardPeriods';

const isWeb = Platform.OS === 'web';

const BackOfficeDashboardScreen: React.FC = () => {
  const { theme } = useTheme();
  const paperTheme = usePaperTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(windowWidth, windowHeight);
  const styles = useMemo(
    () => createStyles(theme, constrainWidth),
    [theme, constrainWidth],
  );
  const axiosBackoffice = useBackofficeAxios();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const { owner } = useBackofficeOwnerAuth();

  const selected = useAppSelector(s => s.backofficeDashboard.selectedRange);
  const totals = useAppSelector(s => s.backofficeDashboard.dashboardData);
  const loading = useAppSelector(s => s.backofficeDashboard.loading);
  const dashError = useAppSelector(s => s.backofficeDashboard.error);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );
  // const previousPeriodLabel = useMemo(() => {
  //   if (!totals?.previousPeriod) return null;
  //   return `Compared to ${moment(totals.previousPeriod.startDate).format(
  //     'MMM D',
  //   )} - ${moment(totals.previousPeriod.endDate).format('MMM D')}`;
  // }, [totals?.previousPeriod]);
  const comparisonPeriodLabel = useMemo(
    () => getDashboardComparisonPeriodLabel(selected),
    [selected],
  );
  const weeklyProducts = useAppSelector(
    s => s.backofficeProducts.WeeklyMostSelling,
  );
  const weeklyChart = useAppSelector(s => s.backofficeReports.weeklySalesData);
  const cat = useAppSelector(s => s.backofficeSales.categorySales);
  const currentAndLast = useAppSelector(
    s => s.backofficeReports.currentAndLastMonth,
  );
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const targets = useAppSelector(s => s.backofficeTargets);
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const paymentBreakdown = useAppSelector(
    s => s.backofficeDashboard.paymentBreakdown,
  );
  const paymentBreakdownLoading = useAppSelector(
    s => s.backofficeDashboard.paymentBreakdownLoading,
  );
  const paymentBreakdownError = useAppSelector(
    s => s.backofficeDashboard.paymentBreakdownError,
  );
  // Ordered method list (by net desc) shared between the summary card and the
  // stacked chart so colors line up in both places.
  const paymentMethodOrder = useMemo<string[]>(
    () => sortMethodsByNetDesc(paymentBreakdown?.by_method).map(m => m.method),
    [paymentBreakdown],
  );

  // Colors are built once from a superset of the current and previous period
  // methods, so the mix card can still color a method that stopped being used.
  const paymentMethodColors = useMemo<Record<string, string>>(() => {
    const previousOnly = sortMethodsByNetDesc(
      paymentBreakdown?.previousPeriod?.by_method,
    )
      .map(m => m.method)
      .filter(m => !paymentMethodOrder.includes(m));
    return buildPaymentMethodColorMap(
      [...paymentMethodOrder, ...previousOnly],
      theme,
    );
  }, [paymentMethodOrder, paymentBreakdown, theme]);

  // Daily/hourly payments-by-method stacked chart — parked for now.
  // const hasPaymentSeriesData = useMemo(() => {
  //   const series = paymentBreakdown?.series || [];
  //   if (!series.length) return false;
  //   let nonZero = 0;
  //   for (const point of series) {
  //     const total = paymentMethodOrder.reduce(
  //       (acc, m) => acc + Number((point as any)?.[m] || 0),
  //       0,
  //     );
  //     if (total > 0) {
  //       nonZero += 1;
  //       if (nonZero >= 2) return true;
  //     }
  //   }
  //   return false;
  // }, [paymentBreakdown, paymentMethodOrder]);

  const ownerId = String(employee?.bp_owner_id);

  // Local UI state only
  const [showTargetDialog, setShowTargetDialog] = useState<boolean>(false);
  const [pendingTargetInput, setPendingTargetInput] = useState<string>('');
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<
    number | null
  >(null);

  const openTargetDialog = useCallback(() => {
    setShowTargetDialog(true);
  }, []);

  // Mirrors the Summary screen's "Due payments" deep link: opens the Orders tab
  // with Completed hidden, so only Pending and Partially Paid are listed.
  // allUnpaid drops the date bounds so the list covers the same all-time
  // balance the card reports.
  const handleViewUnpaidOrders = useCallback(() => {
    navigation.navigate('Orders', {
      preset: 'pending',
      hideCompleted: true,
      allUnpaid: true,
    });
  }, [navigation]);

  useEffect(() => {
    if (!ownerId) return;
    // Require existing owner token; login occurs from HomeScreen before navigation
    if (!owner?.accessToken) return;

    fetchCurrentAndLastMonth();
    fetchFourWeeks();
    fetchSalesTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, owner?.accessToken, locationVersion]);

  const fetchSalesTarget = async () => {
    try {
      if (!owner?.accessToken) return;
      dispatch(setTargetLoading(true));
      const month = Number(moment().format('M'));
      const year = Number(moment().format('YYYY'));
      dispatch(setTargetContext({ month, year }));
      const res = await getMonthlySalesTarget(
        axiosBackoffice as any,
        ownerId,
        month,
        year,
        { accessToken: owner?.accessToken },
      );
      const amount = Number(
        (res as any)?.data?.data?.target ??
        (res as any)?.data?.data?.bp_sales_target_amount ??
        0,
      );
      if (!Number.isNaN(amount) && amount > 0) {
        dispatch(setTargetAmount(amount));
      } else {
        dispatch(setTargetAmount(null));
      }
    } catch (e: any) {
      const status = e?.response?.status || e?.status;
      if (status === 404) {
        dispatch(setTargetAmount(null));
      }
      dispatch(setTargetError(e?.message || 'Failed to fetch target'));
    } finally {
      dispatch(setTargetLoading(false));
    }
  };

  const handleSaveTarget = async () => {
    try {
      const value = Number(String(pendingTargetInput).replace(/[\,\s]/g, ''));
      if (!value || Number.isNaN(value) || value <= 0) return;
      dispatch(setTargetLoading(true));
      const month = Number(moment().format('M'));
      const year = Number(moment().format('YYYY'));
      if (targets?.amount && targets.amount > 0) return;
      // Optimistic update for instant UX and persistence
      dispatch(setTargetContext({ month, year }));
      dispatch(setTargetAmount(value));
      setShowTargetDialog(false);
      setPendingTargetInput('');
      const res = await addMonthlySalesTarget(
        axiosBackoffice as any,
        ownerId,
        month,
        year,
        value,
        {
          accessToken: owner?.accessToken,
        },
      );
      if (!(res?.status === 200)) {
        // Revert if backend rejected
        dispatch(setTargetAmount(null));
      }
    } catch (e) {
      dispatch(setTargetError((e as any)?.message || 'Failed to save target'));
      // Revert on failure
      dispatch(setTargetAmount(null));
    } finally {
      dispatch(setTargetLoading(false));
    }
  };

  const handleChangeTargetInput = (text: string) => {
    try {
      const raw = String(text).replace(/[^\d]/g, '');
      if (!raw) {
        setPendingTargetInput('');
        return;
      }
      const formatted = formatWithCommas(Number(raw), 0);
      setPendingTargetInput(formatted);
    } catch {
      setPendingTargetInput(text);
    }
  };

  useEffect(() => {
    if (targets?.amount && targets.amount > 0) {
      setShowTargetDialog(false);
    }
  }, [targets?.amount]);

  useEffect(() => {
    if (!ownerId) return;
    if (!owner?.accessToken) return;

    const { start, end, periodKey } = getDashboardDateRange(selected);
    fetchDashboard(start, end, periodKey);
    fetchCategorySales(start, end);
    fetchPaymentBreakdown(start, end, periodKey);
    fetchWeeklyMostSelling(1, 20, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, ownerId, owner?.accessToken, locationVersion]);

  const fetchDashboard = async (
    startDate: string,
    endDate: string,
    periodKey: DashboardPeriodKey,
  ) => {
    try {
      dispatch(setDashboardLoading(true));
      dispatch(setDashboardError(null));
      const res = await getDashboardTotals(
        axiosBackoffice as any,
        ownerId,
        startDate,
        endDate,
        { accessToken: owner?.accessToken },
        periodKey,
      );
      console.log('fetchDashboard is', res);
      if (res?.status === 200) {
        dispatch(setDashboardData(res.data.data));
      } else {
        dispatch(setDashboardError(String(res?.data?.message || 'Failed')));
      }
    } catch (e: any) {
      dispatch(setDashboardError(e?.message || 'Error fetching dashboard'));
    } finally {
      dispatch(setDashboardLoading(false));
    }
  };

  const fetchCategorySales = async (startDate: string, endDate: string) => {
    try {
      dispatch(setLoadingCategory(true));
      dispatch(setErrorCategory(null));
      const res = await getCategorySalesReport(
        axiosBackoffice as any,
        ownerId,
        startDate,
        endDate,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        dispatch(setCategorySalesFromApi(res?.data?.data));
      } else {
        dispatch(
          setErrorCategory(
            String(res?.data?.message || 'Failed to load category sales'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setErrorCategory(e?.message || 'Error fetching category sales'));
    } finally {
      dispatch(setLoadingCategory(false));
    }
  };

  const fetchPaymentBreakdown = async (
    startDate: string,
    endDate: string,
    periodKey: DashboardPeriodKey,
  ) => {
    try {
      dispatch(setPaymentBreakdownLoading(true));
      dispatch(setPaymentBreakdownError(null));
      const res = await getPaymentMethodBreakdown(
        axiosBackoffice as any,
        ownerId,
        startDate,
        endDate,
        { accessToken: owner?.accessToken },
        periodKey,
        true, // includeSeries — CollectionPaceCard uses the series for best bucket
      );
      if (res?.status === 200) {
        dispatch(setPaymentBreakdown(res.data.data));
      } else {
        dispatch(
          setPaymentBreakdownError(
            String(
              res?.data?.message || 'Failed to load payment method breakdown',
            ),
          ),
        );
      }
    } catch (e: any) {
      dispatch(
        setPaymentBreakdownError(
          e?.message || 'Error fetching payment method breakdown',
        ),
      );
    } finally {
      dispatch(setPaymentBreakdownLoading(false));
    }
  };

  const fetchCurrentAndLastMonth = async () => {
    try {
      const res = await getCurrentAndLastMonthSales(
        axiosBackoffice as any,
        ownerId,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        dispatch(setCurrentAndLastMonth(res.data.data));
      }
    } catch { }
  };

  const fetchWeeklyMostSelling = async (
    page: number,
    limit: number,
    fromISO: string,
    toISO: string,
  ) => {
    try {
      const res = await apiGetWeeklyMostSelling(
        axiosBackoffice as any,
        ownerId,
        page,
        limit,
        fromISO,
        toISO,
        { accessToken: owner?.accessToken },
      );
      console.log('fetchWeeklyMostSelling is', res);
      if (res?.status === 200) {
        dispatch(setWeeklyMostSelling(res.data.data));
      }
    } catch { }
  };

  const fetchFourWeeks = async () => {
    try {
      const dateToday = moment().endOf('day').toISOString();
      const res = await getLastFourWeeksSales(
        axiosBackoffice as any,
        ownerId,
        dateToday,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        dispatch(setWeeklySalesData(res.data.data));
      }
    } catch { }
  };

  const weeklyChartData = weeklyChart?.chartData || [];
  const top20 = weeklyProducts?.WeeklyMostSelling?.slice(0, 20) || [];

  // Calculate responsive column widths to fit dashboard constraint (WEB_MAX_CONTENT_WIDTH)
  const columns: AppDataTableColumn[] = useMemo(() => {
    if (Platform.OS === 'web') {
      // Dashboard container max width matches WEB_MAX_CONTENT_WIDTH; padding uses same scaling as column widths
      const containerMaxWidth = WEB_MAX_CONTENT_WIDTH;
      const cardPadding = s(12) * 2;
      const contentPadding = s(12);
      const availableWidth =
        Math.min(windowWidth, containerMaxWidth) - cardPadding - contentPadding;

      // # stays tight; product capped; leftover split evenly across the rest
      const indexWidth = s(44);
      const productWidth = Math.min(
        s(280),
        Math.max(s(180), availableWidth * 0.32),
      );
      const otherCount = 3;
      const otherWidth = Math.max(
        s(110),
        (availableWidth - indexWidth - productWidth) / otherCount,
      );

      return [
        { title: '#', width: indexWidth, align: 'center' },
        {
          title: 'Product',
          width: productWidth,
          wrap: true,
          align: 'left',
        },
        {
          title: 'Selling Price',
          width: otherWidth,
          align: 'center',
        },
        {
          title: 'Quantity',
          width: otherWidth,
          align: 'center',
        },
        {
          title: 'Sales',
          width: otherWidth,
          align: 'center',
        },
      ];
    }
    // Native widths
    return [
      { title: '#', width: s(40) },
      { title: 'Product', width: s(220) },
      { title: 'Sales', width: s(120) },
      { title: 'Quantity', width: s(110) },
      {
        title: 'Selling Price',
        width: s(120),
      },
    ];
  }, [windowWidth]);

  const tableData: Array<Array<React.ReactNode>> = top20.map((p, idx) => {
    const isUnit =
      String(p?.bp_product_price_type || '').toLowerCase() === 'unit';
    const massSold = Number(p?.MassSold ?? 0);
    const qtySold = Number(p?.Quantity ?? 0);
    const qtyLabel = isUnit
      ? `${formatWithCommas(massSold, getNumericDisplayDecimals(massSold), true)} ${String(
        p?.bp_product_unit_of_mass || '',
      )}`
      : formatWithCommas(qtySold, 0, true);
    // const buying = formatWithCommas(Number(p?.bp_product_buying_price || 0), 0);
    const sellingPrice = Number(p?.bp_product_selling_price || 0);
    const selling = formatWithCommas(
      sellingPrice,
      getNumericDisplayDecimals(sellingPrice),
      true,
    );
    const salesAmount = Number(p?.Sales || 0);
    const sales = formatWithCommas(
      salesAmount,
      getNumericDisplayDecimals(salesAmount),
      true,
    );
    if (Platform.OS !== 'web') {
      return [
        String(idx + 1),
        p?.bp_product_name || '',
        sales,
        qtyLabel,
        selling,
      ];
    }
    return [
      String(idx + 1),
      p?.bp_product_name || '',
      // p?.bp_product_sub_category || '',
      // buying,
      selling,
      qtyLabel,
      sales,
    ];
  });

  // Auth gating: don't render cards until logged in. No dashboard-level spinner for login.
  if (!owner?.accessToken) {
    return (
      <View
        style={[
          styles.container,
          // { paddingTop: Math.max(insets.top, v(4)) },
        ]}>
        <BackOfficeFallback
          title="Owner authentication required"
          message={owner?.error || 'Please login from Home > Back Office'}
          primaryActionLabel="Go Back"
          onPrimaryAction={() => {
            return;
          }}
        />
      </View>
    );
  }

  return (
    <View
      style={[
        styles.container,
        // { paddingTop: Math.max(insets.top, v(4)) },
      ]}>
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        {dashError ? (
          <BackOfficeFallback
            title="Failed to load stats"
            message={dashError}
            primaryActionLabel="Retry"
            onPrimaryAction={() => {
              const { start, end, periodKey } = getDashboardDateRange(selected);
              fetchDashboard(start, end, periodKey);
            }}
          />
        ) : (
          <>
            {/* {loading ? (
              <View style={styles.inlineLoading}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text style={styles.inlineLoadingText}>Updating stats…</Text>
              </View>
            ) : null} */}

            {/* Payment method breakdown: what came in, pace + method change,
                and what is still outstanding. */}
            <MoneyCollectedCard
              data={paymentBreakdown}
              loading={paymentBreakdownLoading && !paymentBreakdown}
              error={paymentBreakdownError}
              currencyText={currencyText}
              methodColors={paymentMethodColors}
              comparisonPeriodLabel={comparisonPeriodLabel}
              target={{
                targetAmount: targets?.amount ?? null,
                currentMonthlySales: Number(
                  currentAndLast?.CurrentMonthSales || 0,
                ),
                targetLoading: !!targets?.loading,
                onAddTarget: openTargetDialog,
              }}
            />

            {/* Featured Section — web-only; on native the target is merged
                into MoneyCollectedCard above. */}
            {isWeb ? (
              <View style={[styles.card]}>
                <View
                  style={{
                    alignItems: 'center',
                    marginBottom: v(10),
                  }}>
                  <Text style={styles.cardTitle}>
                    Monthly Sales Performance
                  </Text>
                  <Text
                    style={{
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(22),
                      lineHeight: fs(26),
                      color: theme.colors.text,
                    }}>
                    {currencyText}
                    {'\u00A0'}
                    {formatWithCommas(
                      Number(currentAndLast?.CurrentMonthSales || 0),
                      0,
                    )}
                  </Text>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    gap: s(16) as unknown as number,
                    alignItems: 'center',
                    borderRadius: theme.borderRadius.md,
                    margin: v(10),
                  }}>
                  {/* Target */}
                  <View style={styles.featuredCol}>
                    <Text style={styles.featuredLabel}>Target</Text>
                    {targets?.amount && targets.amount > 0 ? (
                      <View style={{ alignItems: 'center' }}>
                        <Text style={styles.featuredValue}>
                          {currencyText} {formatWithCommas(targets.amount, 0)}
                        </Text>
                      </View>
                    ) : (
                      <GreenActionButton
                        amount={targets?.amount || 0}
                        label="Set Target"
                        icon="add"
                        onPress={openTargetDialog}
                        loading={!!targets?.loading && targets?.amount == null}
                      />
                    )}
                  </View>

                  {/* Current Sales */}
                  <View style={styles.featuredCol}>
                    <Text style={styles.featuredLabel}>Current Sales</Text>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <MaterialCommunityIcons
                        name={
                          Number(currentAndLast?.CurrentMonthSales || 0) >=
                          Number(currentAndLast?.LastMonthSales || 0)
                            ? 'arrow-up-bold'
                            : 'arrow-down-bold'
                        }
                        size={iconSize(16)}
                        color={
                          Number(currentAndLast?.CurrentMonthSales || 0) >=
                          Number(currentAndLast?.LastMonthSales || 0)
                            ? theme.colors.success
                            : theme.colors.error
                        }
                        style={{ marginRight: s(4) }}
                      />
                      <Text
                        style={[
                          styles.featuredValue,
                          {
                            color:
                              Number(currentAndLast?.CurrentMonthSales || 0) >=
                              Number(currentAndLast?.LastMonthSales || 0)
                                ? theme.colors.success
                                : theme.colors.error,
                          },
                        ]}>
                        {currencyText}
                        {'\u00A0'}
                        {formatWithCommas(
                          Number(currentAndLast?.CurrentMonthSales || 0),
                          0,
                        )}
                      </Text>
                    </View>
                  </View>

                  {/* Last Month */}
                  <View style={styles.featuredCol}>
                    <Text style={styles.featuredLabel}>Last Month</Text>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <MaterialCommunityIcons
                        name={
                          Number(currentAndLast?.LastMonthSales || 0) >=
                          Number(targets?.amount || 0)
                            ? 'arrow-up-bold'
                            : 'arrow-down-bold'
                        }
                        size={iconSize(16)}
                        color={
                          Number(currentAndLast?.LastMonthSales || 0) >=
                          Number(targets?.amount || 0)
                            ? theme.colors.success
                            : theme.colors.error
                        }
                        style={{ marginRight: s(4) }}
                      />
                      <Text
                        style={[
                          styles.featuredValue,
                          {
                            color:
                              Number(currentAndLast?.LastMonthSales || 0) >=
                              Number(targets?.amount || 0)
                                ? theme.colors.success
                                : theme.colors.error,
                          },
                        ]}>
                        {currencyText}
                        {'\u00A0'}
                        {formatWithCommas(
                          Number(currentAndLast?.LastMonthSales || 0),
                          0,
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            ) : null}

            {/* No loading gate: the outstanding balance is a running total, so
                it does not go stale when the period selector changes. */}
            <StillOwedCard
              data={paymentBreakdown}
              currencyText={currencyText}
              onViewUnpaidOrders={handleViewUnpaidOrders}
            />

            {/* Daily payments by method — doesn't fit this section for now.
            {hasPaymentSeriesData ? (
              <View style={[styles.card]}>
                <Text style={styles.cardTitle}>
                  {getSeriesChartTitle(paymentBreakdown)}
                </Text>
                <PaymentMethodStackedChart
                  series={paymentBreakdown?.series || []}
                  methodColors={paymentMethodColors}
                  methodOrder={paymentMethodOrder}
                  currencyText={currencyText}
                />
              </View>
            ) : null}
            */}

            <View style={styles.widgetsRow3Col}>
              <StatCard
                label="Sales"
                icon="cash-multiple"
                iconColor={theme.colors.successEmerald}
                valuePrefix={currencyText}
                value={`${formatWithCommas(totals?.totalSales || 0)}`}
                comparison={totals?.comparison?.totalSales}
                comparisonPeriodLabel={comparisonPeriodLabel}
                loading={loading && !totals}
              />
              <StatCard
                label="Expenses"
                icon="cash-plus"
                iconColor={theme.colors.error}
                valuePrefix={currencyText}
                value={`${formatWithCommas(totals?.totalExpenses || 0)}`}
                comparison={totals?.comparison?.totalExpenses}
                comparisonPeriodLabel={comparisonPeriodLabel}
                loading={loading && !totals}
              />
              <StatCard
                label="Profit"
                icon="trending-up"
                iconColor={theme.colors.primary}
                valuePrefix={currencyText}
                value={`${formatWithCommas(totals?.totalProfit || 0)}`}
                comparison={totals?.comparison?.totalProfit}
                comparisonPeriodLabel={comparisonPeriodLabel}
                loading={loading && !totals}
              />
              <StatCard
                label="Orders"
                icon="receipt-text-outline"
                iconColor={theme.colors.primary}
                value={`${formatWithCommas(totals?.totalOrders || 0)}`}
                comparison={totals?.comparison?.totalOrders}
                comparisonPeriodLabel={comparisonPeriodLabel}
                loading={loading && !totals}
              />
              {/* <StatCard
                label="Quantity Sold"
                value={`${formatWithCommas(totals?.totalQuantitiesSold || 0)}`}
              />
              <StatCard
                label="Mass Sold"
                value={`${formatNumberDecimal(totals?.totalMassSold || 0)} Kg|Lt|Mg`}
              /> */}
            </View>
          </>
        )}

        {/* Set Target Dialog - web parity (no editing after set) */}

        {showTargetDialog && (
          <Portal>
            <KeyboardAvoidingView
              style={styles.keyboardAvoidingView}
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
              <Dialog
                visible={showTargetDialog}
                onDismiss={() => setShowTargetDialog(false)}
                style={[
                  styles.targetDialog,
                  { backgroundColor: paperTheme.colors.surfaceVariant },
                ]}>
                <Dialog.Title>Set Monthly Collection Target</Dialog.Title>
                <Dialog.Content style={styles.targetDialogContent}>
                  <TextInput
                    mode="outlined"
                    label={`Target amount (${currencyText})`}
                    value={pendingTargetInput}
                    onChangeText={handleChangeTargetInput}
                    keyboardType="numeric"
                    style={styles.targetDialogInput}
                  />
                </Dialog.Content>
                <Dialog.Actions>
                  <Button
                    onPress={() => setShowTargetDialog(false)}
                    textColor={theme.colors.errorDark}>
                    Cancel
                  </Button>
                  {!(targets?.amount && targets.amount > 0) && (
                    <Button
                      loading={!!targets?.loading}
                      disabled={!!targets?.loading}
                      textColor={
                        pendingTargetInput
                          ? theme.colors.successEmerald
                          : theme.colors.textSecondary
                      }
                      onPress={handleSaveTarget}>
                      Set Target
                    </Button>
                  )}
                </Dialog.Actions>
              </Dialog>
            </KeyboardAvoidingView>
          </Portal>
        )}

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Monthly Sales Comparison</Text>
          <WeeklyComparisonChart data={weeklyChartData as any} />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Sales by Category</Text>
          <CategoryDonutChart
            selectedCategoryIndex={selectedCategoryIndex}
            onCategoryPress={index =>
              setSelectedCategoryIndex(prev =>
                prev === index ? null : index,
              )
            }
            data={(cat || []).map(c => ({ label: c.name, value: c.sales }))}
          />
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>
            Top 20 Selling Products {getDashboardRangeDisplayLabel(selected)}
          </Text>
          <View
            style={{
              marginHorizontal: s(8),
              marginBottom: v(8),
            }}>
            <Text style={styles.cardSub}>
              Total sales: {currencyText}
              {'\u00A0'}
              {formatWithCommas(
                Number(weeklyProducts?.metadata?.totalSales || 0),
                0,
              )}
            </Text>
            <Text style={styles.cardSub}>
              Total Quantity Sold:{'\u00A0'}
              {formatWithCommas(
                Number(weeklyProducts?.metadata?.totalItems || 0),
                0,
              )}
            </Text>
            {Number(weeklyProducts?.metadata?.totalMassSold || 0) > 0 ? (
              <Text style={styles.cardSub}>
                Total Mass Sold:{'\u00A0'}
                {formatNumberDecimal(
                  Number(weeklyProducts?.metadata?.totalMassSold || 0),
                )}
                {'\u00A0'}
                Kg/L/M
              </Text>
            ) : null}
          </View>
          <AppDataTable
            columns={columns}
            data={tableData}
            loading={false}
            showRotateToggle={false}
            // maxBodyHeight={v(360)}
            emptyMessage="No products found"
          />
        </View>
      </ScrollView>
    </View>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  valuePrefix?: string;
  valueSuffix?: string;
  comparison?: DashboardComparisonMetric;
  comparisonPeriodLabel?: string;
  loading?: boolean;
  icon?: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  iconColor?: string;
}> = ({
  label,
  value,
  valuePrefix,
  valueSuffix,
  comparison,
  comparisonPeriodLabel,
  loading = false,
  icon,
  iconColor,
}) => {
    const { theme } = useTheme();
    const badgeColor = iconColor || theme.colors.primary;
    const comparisonChange = Number(comparison?.change ?? 0);
    const comparisonPercentage = Number(comparison?.percentageChange ?? 0);
    const hasComparison = Boolean(comparison);
    const shouldShowComparison = hasComparison || loading;
    const shouldShowZeroInsteadOfNegativeHundred = comparisonPercentage === -100;
    const displayComparisonPercentage = shouldShowZeroInsteadOfNegativeHundred
      ? 0
      : comparisonPercentage;
    const isPositive =
      comparisonChange >= 0 || shouldShowZeroInsteadOfNegativeHundred;
    const comparisonSign = isPositive ? '+' : '-';
    const comparisonColor = isPositive
      ? theme.colors.successEmerald
      : theme.colors.error;

    const renderSkeleton = (width: number, height: number) => (
      <SkeletonPlaceholder
        backgroundColor={theme.colors.border}
        highlightColor={theme.colors.surfaceSecondary}>
        <SkeletonPlaceholder.Item
          width={width}
          height={height}
          borderRadius={s(4)}
        />
      </SkeletonPlaceholder>
    );

    return (
      <View
        style={{
          backgroundColor: theme.colors.surface,
          padding: s(12),
          borderRadius: theme.borderRadius.md,
          flexBasis: '48%',
          width: '48%',
          maxWidth: '48%',
          ...theme.shadows.sm,
          ...(isWeb
            ? {
              minHeight: v(80),
            }
            : {}),
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: s(6),
          }}>
          {icon ? (
            <View
              style={{
                width: s(24),
                height: s(24),
                borderRadius: s(7),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: badgeColor + '1A',
              }}>
              <MaterialCommunityIcons
                name={icon}
                size={fs(15)}
                color={badgeColor}
              />
            </View>
          ) : null}
          <Text
            style={{
              flexShrink: 1,
              color: theme.colors.text,
              fontFamily: GTFonts.bold,
              fontSize: fs(14),
              lineHeight: fs(16),
            }}>
            {label}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: s(4),
            marginTop: v(10),
          }}>
          {valuePrefix ? (
            <Text
              style={{
                color: theme.colors.text,
                fontFamily: GTFonts.semibold,
                fontSize: fs(18),
                lineHeight: fs(20),
              }}>
              {valuePrefix}
            </Text>
          ) : null}
          {loading ? (
            renderSkeleton(s(72), v(18))
          ) : (
            <Text
              style={{
                color: theme.colors.text,
                fontFamily: GTFonts.semibold,
                fontSize: fs(18),
                lineHeight: fs(20),
              }}>
              {value}
            </Text>
          )}
          {valueSuffix ? (
            <Text
              style={{
                color: theme.colors.text,
                fontFamily: GTFonts.semibold,
                fontSize: fs(18),
                lineHeight: fs(20),
              }}>
              {valueSuffix}
            </Text>
          ) : null}
        </View>
        {shouldShowComparison ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: s(4),
              marginTop: v(6),
              flexWrap: 'wrap',
            }}>
            {loading ? (
              renderSkeleton(s(48), v(12))
            ) : (
              <Text
                style={{
                  color: comparisonColor,
                  fontFamily: GTFonts.semibold,
                  fontSize: fs(12),
                  lineHeight: fs(16),
                }}>
                {comparisonSign}
                {formatNumberDecimal(Math.abs(displayComparisonPercentage))}%
              </Text>
            )}
            {comparisonPeriodLabel ? (
              <Text
                style={{
                  color: theme.colors.textSecondary,
                  fontFamily: GTFonts.semibold,
                  fontSize: fs(12),
                  lineHeight: fs(16),
                }}>
                vs {comparisonPeriodLabel}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  };

export default BackOfficeDashboardScreen;

const createStyles = (theme: any, constrainWidth: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      // padding: s(6),
      backgroundColor: theme.colors.background,
      ...(isWeb && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    keyboardAvoidingView: {
      flex: 1,
      justifyContent: 'center',
    },
    title: {
      fontSize: fs(20),
      lineHeight: fs(24),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    loadingWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    inlineLoading: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(8),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingVertical: s(6),
      paddingHorizontal: s(8),
      ...theme.shadows.sm,
    },
    inlineLoadingText: {
      marginLeft: s(6),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
    },
    content: {
      // padding: s(5),
      paddingBottom: s(20),
      rowGap: v(8),
    },
    widgetsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(10) as unknown as number,
      marginBottom: v(8),
    },
    widgetsRow3Col: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      rowGap: v(10),
      marginTop: v(6),
      marginHorizontal: s(10),
    },
    comparisonCaption: {
      width: '100%',
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      marginBottom: v(2),
    },
    row: {
      flexDirection: 'row',
      gap: s(12) as unknown as number,
    },
    colLeft: {
      flex: 1.8,
      gap: s(12) as unknown as number,
    },
    colRight: {
      flex: 2.2,
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      marginTop: v(5),
      marginHorizontal: s(10),
      ...theme.shadows.sm,
      ...(isWeb
        ? {
          minHeight: v(180),
        }
        : {}),
    },
    cardTitle: {
      fontFamily: GTFonts.bold,
      textAlign: 'center',
      color: theme.colors.text,
      fontSize: fs(18),
      lineHeight: fs(20),
      marginVertical: v(12),
      ...(isWeb
        ? {

        }
        : {}),
    },
    cardSub: {
      color: theme.colors.text,
      lineHeight: fs(16),
      fontFamily: GTFonts.regular,
    },
    sectionTitle: {
      color: theme.colors.text,
      textAlign: 'center',
      fontFamily: GTFonts.bold,
      fontSize: fs(18),
      lineHeight: fs(20),
      marginVertical: v(12),
      marginBottom: v(16),
      ...(isWeb
        ? {
        }
        : {}),
    },
    featuredCol: {
      flex: 1,
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.borderRadius.sm,
      paddingVertical: s(10),
      paddingHorizontal: s(8),
      alignItems: 'center',
      justifyContent: 'center',
    },
    featuredLabel: {
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      fontSize: fs(12),
      lineHeight: fs(16),
      marginBottom: v(6),
    },
    featuredValue: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
      textAlign: 'center',
    },
    targetDialog: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      ...theme.shadows.sm,
      ...(constrainWidth
        ? {
          width: '100%',
          alignSelf: 'center',
          maxWidth: WEB_MODAL_MAX_WIDTH,
        }
        : {}),
    },
    targetDialogContent: {
      justifyContent: 'center',
    },
    targetDialogInput: {
      height: v(48),
    },
  });
